import { useState, useCallback, useEffect } from "react";
import Logger from "../utils/logger";

const FULLSCREEN_EVENTS = [
  "fullscreenchange",
  "webkitfullscreenchange",
  "mozfullscreenchange",
  "MSFullscreenChange",
] as const;

function getFullscreenElement(): Element | null {
  const doc = document as Document & {
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  };

  return (
    doc.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

function getExitFullscreen(): (() => Promise<void>) | null {
  const doc = document as Document & {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
  };

  return (
    doc.exitFullscreen?.bind(doc) ??
    doc.webkitExitFullscreen?.bind(doc) ??
    doc.mozCancelFullScreen?.bind(doc) ??
    doc.msExitFullscreen?.bind(doc) ??
    null
  );
}

function getRequestFullscreen(
  element: HTMLElement
): (() => Promise<void>) | null {
  const el = element as HTMLElement & {
    mozRequestFullScreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
  };

  return (
    el.requestFullscreen?.bind(el) ??
    el.webkitRequestFullscreen?.bind(el) ??
    el.mozRequestFullScreen?.bind(el) ??
    el.msRequestFullscreen?.bind(el) ??
    null
  );
}

export function useFullscreen(elementRef: React.RefObject<HTMLElement>) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const togglePseudoFullscreen = useCallback(
    (forceState?: boolean) => {
      const newState = forceState ?? !isFullscreen;
      setIsFullscreen(newState);
      if (newState) {
        document.body.classList.add("ios-pseudo-fullscreen");
      } else {
        document.body.classList.remove("ios-pseudo-fullscreen");
      }
    },
    [isFullscreen]
  );

  const enter = useCallback(async () => {
    if (!elementRef.current) return;
    const requestFullscreen = getRequestFullscreen(elementRef.current);

    if (requestFullscreen) {
      try {
        await requestFullscreen();
      } catch (err) {
        Logger.error(`Fullscreen enter error: ${err}`);
      }
    } else {
      togglePseudoFullscreen(true);
    }
  }, [elementRef, togglePseudoFullscreen]);

  const exit = useCallback(async () => {
    const exitFullscreen = getExitFullscreen();
    if (exitFullscreen && getFullscreenElement()) {
      try {
        await exitFullscreen();
      } catch (err) {
        Logger.error(`Fullscreen exit error: ${err}`);
      }
    } else {
      togglePseudoFullscreen(false);
    }
  }, [togglePseudoFullscreen]);

  const toggle = useCallback(async () => {
    if (!elementRef.current) {
      Logger.warn("Element ref not attached");
      return;
    }

    if (
      getFullscreenElement() ||
      document.body.classList.contains("ios-pseudo-fullscreen")
    ) {
      await exit();
    } else {
      await enter();
    }
  }, [enter, exit, elementRef]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNative = getFullscreenElement() === elementRef.current;
      const isPseudo = document.body.classList.contains(
        "ios-pseudo-fullscreen"
      );
      setIsFullscreen(isNative || isPseudo);
    };

    FULLSCREEN_EVENTS.forEach((event) => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      FULLSCREEN_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleFullscreenChange);
      });
      document.body.classList.remove("ios-pseudo-fullscreen");
    };
  }, [elementRef]);

  return { isFullscreen, toggle, enter, exit } as const;
}
